/**
 * Date Validation Utilities
 * 
 * This module provides utilities for date format validation and range checking
 * for TEFAS API requests.
 */

import { InvalidDateError, DateRangeError } from '../errors';
import { type DateRangeValidation } from '../types';
import * as chrono from 'chrono-node';

/**
 * ISO 8601 date format regex (YYYY-MM-DD)
 */
const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Validates if a date string is in ISO 8601 format (YYYY-MM-DD)
 * 
 * @param date - Date string to validate
 * @returns True if valid, false otherwise
 */
export function isValidDateFormat(date: string): boolean {
  if (!ISO_DATE_REGEX.test(date)) {
    return false;
  }

  // Check if the date is actually valid
  const parsedDate = new Date(date);
  return !isNaN(parsedDate.getTime()) && parsedDate.toISOString().split('T')[0] === date;
}

/**
 * Validates a date string and throws an error if invalid
 * 
 * @param date - Date string to validate
 * @param fieldName - Name of the field for error messages
 * @throws InvalidDateError if date is invalid
 */
export function validateDate(date: string, fieldName: string = 'date'): void {
  if (!date || typeof date !== 'string') {
    throw new InvalidDateError(date, `${fieldName} must be a non-empty string`);
  }

  if (!ISO_DATE_REGEX.test(date)) {
    throw new InvalidDateError(date, `${fieldName} must be in YYYY-MM-DD format`);
  }

  const parsedDate = new Date(date);
  if (isNaN(parsedDate.getTime())) {
    throw new InvalidDateError(date, `${fieldName} is not a valid date`);
  }

  // Check if the parsed date matches the input string (prevents dates like 2024-02-30)
  if (parsedDate.toISOString().split('T')[0] !== date) {
    throw new InvalidDateError(date, `${fieldName} is not a valid date`);
  }
}

/**
 * Parses a date string to a Date object
 * 
 * @param date - Date string in YYYY-MM-DD format
 * @returns Parsed Date object
 * @throws InvalidDateError if date is invalid
 */
export function parseDate(date: string): Date {
  validateDate(date);
  return new Date(date);
}

/**
 * Formats a date to YYYY-MM-DD string
 * 
 * @param date - Date object to format
 * @returns Formatted date string
 */
export function formatDate(date: Date): string {
  return date.toISOString().split('T')[0];
}

/**
 * Gets the current date in YYYY-MM-DD format
 * 
 * @returns Current date string
 */
export function getCurrentDate(): string {
  return formatDate(new Date());
}

/**
 * Adds days to a date
 * 
 * @param date - Date string in YYYY-MM-DD format
 * @param days - Number of days to add (can be negative)
 * @returns New date string in YYYY-MM-DD format
 */
export function addDays(date: string, days: number): string {
  const parsedDate = parseDate(date);
  const newDate = new Date(parsedDate);
  newDate.setDate(newDate.getDate() + days);
  return formatDate(newDate);
}

/**
 * Calculates the number of days between two dates
 * 
 * @param startDate - Start date string
 * @param endDate - End date string
 * @returns Number of days between dates
 */
export function getDaysBetween(startDate: string, endDate: string): number {
  const start = parseDate(startDate);
  const end = parseDate(endDate);
  return Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
}

/**
 * Checks if a date is in the past
 * 
 * @param date - Date string to check
 * @returns True if date is in the past
 */
export function isPastDate(date: string): boolean {
  const parsedDate = parseDate(date);
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Reset time to start of day
  return parsedDate < today;
}

/**
 * Checks if a date is in the future
 * 
 * @param date - Date string to check
 * @returns True if date is in the future
 */
export function isFutureDate(date: string): boolean {
  const parsedDate = parseDate(date);
  const today = new Date();
  today.setHours(23, 59, 59, 999); // Set time to end of day
  return parsedDate > today;
}

/**
 * Validates a date range and throws an error if invalid
 * 
 * @param startDate - Start date string in YYYY-MM-DD format
 * @param endDate - End date string in YYYY-MM-DD format
 * @param maxDays - Maximum allowed days in range (optional, no limit if not provided)
 * @throws InvalidDateError if either date format is invalid
 * @throws DateRangeError if date range is invalid
 */
export function validateDateRange(startDate: string, endDate: string, maxDays?: number): void {
  // First validate individual dates
  validateDate(startDate, 'start date');
  validateDate(endDate, 'end date');

  // Parse dates for comparison
  const start = parseDate(startDate);
  const end = parseDate(endDate);

  // Check if start date is after end date
  if (start > end) {
    throw new DateRangeError(startDate, endDate, 'Start date must be before end date');
  }

  // Check if date range exceeds maximum allowed days (only if maxDays is provided)
  if (maxDays !== undefined) {
    const daysBetween = getDaysBetween(startDate, endDate);
    if (daysBetween > maxDays) {
      throw new DateRangeError(startDate, endDate, `Date range cannot exceed ${maxDays} days`);
    }
  }
}

/**
 * Validates that start date is not more than 5 years ago (TEFAS API constraint)
 *
 * TEFAS API has a Web Application Firewall (WAF) that rejects requests with
 * startDate earlier than 5 years ago. We add a 1-day buffer for safety.
 *
 * @param startDate - Start date string in YYYY-MM-DD format
 * @throws InvalidDateError if date format is invalid
 * @throws DateRangeError if startDate is earlier than 5 years ago + 1 day buffer
 *
 * @example
 * ```typescript
 * // Today is 2025-11-22
 * validateTefasDateLimit('2020-11-23'); // Valid (exactly 5 years ago + 1 day)
 * validateTefasDateLimit('2020-11-22'); // Throws DateRangeError (too far back)
 * validateTefasDateLimit('2020-11-20'); // Throws DateRangeError (too far back)
 * ```
 */
export function validateTefasDateLimit(startDate: string): void {
  // First validate date format
  validateDate(startDate, 'start date');

  // Calculate 5 years ago + 1 day buffer
  const fiveYearsAgo = new Date();
  fiveYearsAgo.setFullYear(fiveYearsAgo.getFullYear() - 5);
  fiveYearsAgo.setDate(fiveYearsAgo.getDate() + 1); // +1 day buffer

  const start = parseDate(startDate);

  if (start < fiveYearsAgo) {
    const limitDate = formatDate(fiveYearsAgo);
    throw new DateRangeError(
      startDate,
      limitDate,
      `TEFAS API does not support dates earlier than 5 years ago. Start date "${startDate}" is too far back. Earliest allowed date: ${limitDate}`
    );
  }
}

/**
 * Converts a date from YYYY-MM-DD format to DD.MM.YYYY format for TEFAS API
 *
 * @param date - Date string in YYYY-MM-DD format
 * @returns Date string in DD.MM.YYYY format
 * @throws InvalidDateError if date is invalid
 */
export function convertToTefasDateFormat(date: string): string {
  validateDate(date);
  const [year, month, day] = date.split('-');
  return `${day}.${month}.${year}`;
}

/**
 * Parses a fuzzy date string (e.g., "today", "yesterday", "2024-01-01") to a normalized YYYY-MM-DD format
 * 
 * @param dateInput - Fuzzy date string (e.g., "today", "yesterday", "2024-01-01", "last week")
 * @param referenceDate - Optional reference date for relative date parsing (defaults to current date)
 * @returns Normalized date string in YYYY-MM-DD format
 * @throws InvalidDateError if date cannot be parsed
 * 
 * @example
 * ```typescript
 * parseFuzzyDate('today'); // Returns current date in YYYY-MM-DD format
 * parseFuzzyDate('yesterday'); // Returns yesterday's date in YYYY-MM-DD format
 * parseFuzzyDate('2024-01-01'); // Returns '2024-01-01'
 * parseFuzzyDate('last week'); // Returns date from last week
 * ```
 */
export function parseFuzzyDate(dateInput: string, referenceDate?: Date): string {
  if (!dateInput || typeof dateInput !== 'string') {
    throw new InvalidDateError(dateInput, 'Date input must be a non-empty string');
  }

  const trimmedInput = dateInput.trim();
  
  // If it's already in YYYY-MM-DD format, validate and return as-is
  if (ISO_DATE_REGEX.test(trimmedInput)) {
    validateDate(trimmedInput);
    return trimmedInput;
  }

  // Use chrono to parse fuzzy dates
  const refDate = referenceDate || new Date();
  const parsedResult = chrono.parseDate(trimmedInput, refDate);

  if (!parsedResult) {
    throw new InvalidDateError(dateInput, `Unable to parse date: "${dateInput}". Supported formats include: "today", "yesterday", "2024-01-01", "last week", etc.`);
  }

  // Convert to YYYY-MM-DD format
  return formatDate(parsedResult);
}

/**
 * Validates and normalizes fuzzy date inputs for TEFAS API
 * 
 * @param startDate - Start date (fuzzy or YYYY-MM-DD format)
 * @param endDate - End date (fuzzy or YYYY-MM-DD format)
 * @param maxDays - Maximum allowed days in range (optional, no limit if not provided)
 * @returns Object containing normalized start and end dates
 * @throws InvalidDateError if either date cannot be parsed
 * @throws DateRangeError if date range is invalid
 * 
 * @example
 * ```typescript
 * const { startDate, endDate } = normalizeFuzzyDateRange('today', 'yesterday');
 * // Throws DateRangeError because start > end
 * 
 * const { startDate, endDate } = normalizeFuzzyDateRange('yesterday', 'today');
 * // Returns normalized dates
 * ```
 */
export function normalizeFuzzyDateRange(
  startDate: string, 
  endDate: string, 
  maxDays?: number
): { startDate: string; endDate: string } {
  // Parse fuzzy dates to normalized format
  const normalizedStartDate = parseFuzzyDate(startDate);
  const normalizedEndDate = parseFuzzyDate(endDate);

  // Validate the date range
  validateDateRange(normalizedStartDate, normalizedEndDate, maxDays);

  return {
    startDate: normalizedStartDate,
    endDate: normalizedEndDate
  };
}
